import { useElementMetadata } from "@/components/contexts/ElementMetadataContext";
import { useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { usePreviewUpdateStore } from "@/store/zustand/previewUpdateStore";
import { getPreviewUrlForElement } from "@/utils/imageUtils";
import { useActiveProject } from "@/utils/utils";
import type { JsxElement } from "@/utils/jsxElementTypes";

export const useImagePreview = (element: JsxElement) => {
  const { metadata } = useElementMetadata();
  const project = useActiveProject();
  const elementsToFetch = useSelector(
    (state: { metadata: { elementsToFetch: string[] } }) =>
      state.metadata.elementsToFetch
  );

  // Listen for preview updates to invalidate cache selectively
  const { lastPreviewUpdate, hasComponentUpdate, clearUpdatedComponent } =
    usePreviewUpdateStore((state) => ({
      lastPreviewUpdate: state.lastPreviewUpdate,
      hasComponentUpdate: state.hasComponentUpdate,
      clearUpdatedComponent: state.clearUpdatedComponent,
    }));

  const { imagePath, metadataStatus } = useMemo(() => {
    const fileHash = element.fileHash;

    const metadataStatus =
      fileHash && elementsToFetch.includes(fileHash) ? "pending" : "done";

    let imagePath = getPreviewUrlForElement(element, project.id);

    // Use metadata as fallback only when getPreviewUrlForElement returns no imagePath
    if (!imagePath && fileHash && metadata && metadata[fileHash]) {
      imagePath = metadata[fileHash].previewUrl || null;
    }

    // Add additional logic for specific element types
    if (element?.type === "storybook-component") {
      const storyName = element?.filename
        ? element.filename.split("/").pop()
        : undefined;
      const { componentName, library } = element;
      if (library && storyName && componentName) {
        imagePath = `https://storage.googleapis.com/tempo-image-previews/${library}/${storyName}/${componentName}.png`;
      }
    }

    return { imagePath, metadataStatus };
  }, [element, metadata, elementsToFetch, project]);

  // Clear cache selectively when specific components are updated
  useEffect(() => {
    if (lastPreviewUpdate > 0) {
      const fileHashKey = element.fileHash;

      // Check if this component was updated using the fileHash
      const wasUpdatedWithFileHash =
        fileHashKey && hasComponentUpdate(fileHashKey);

      if (wasUpdatedWithFileHash) {
        // Mark this component as processed
        clearUpdatedComponent(fileHashKey);
      }
    }
  }, [
    lastPreviewUpdate,
    element.fileHash,
    hasComponentUpdate,
    clearUpdatedComponent,
  ]);

  const finalValues = useMemo(() => {
    const finalImagePath = imagePath;

    // Add cache busting for preview URLs when we have a recent update
    if (
      finalImagePath?.includes("component-previews/") &&
      lastPreviewUpdate > 0
    ) {
      // Check if this component was recently updated (within last 30 seconds)
      const timeSinceUpdate = Date.now() - lastPreviewUpdate;
      if (timeSinceUpdate < 30000) {
        // Add cache busting parameter to ensure fresh image load
        const separator = finalImagePath.includes("?") ? "&" : "?";
        const cachedImagePath = `${finalImagePath}${separator}t=${lastPreviewUpdate}`;

        return {
          imagePath: cachedImagePath,
          metadataStatus,
        };
      }
    }

    return {
      imagePath: finalImagePath,
      metadataStatus,
    };
  }, [imagePath, metadataStatus, lastPreviewUpdate]);

  return finalValues;
};
